import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle,
  Wind, Droplets, MapPin,
} from "lucide-react";

// WMO weather code → { icon, label(en,es) }
const CODE_META = {
  0:  { icon: Sun,           en: "Clear",         es: "Despejado" },
  1:  { icon: Sun,           en: "Mostly clear",  es: "Poco nublado" },
  2:  { icon: Cloud,         en: "Partly cloudy", es: "Parcialmente nublado" },
  3:  { icon: Cloud,         en: "Overcast",      es: "Nublado" },
  45: { icon: CloudFog,      en: "Fog",           es: "Niebla" },
  48: { icon: CloudFog,      en: "Rime fog",      es: "Niebla escarcha" },
  51: { icon: CloudDrizzle,  en: "Light drizzle", es: "Llovizna ligera" },
  53: { icon: CloudDrizzle,  en: "Drizzle",       es: "Llovizna" },
  55: { icon: CloudDrizzle,  en: "Heavy drizzle", es: "Llovizna intensa" },
  61: { icon: CloudRain,     en: "Light rain",    es: "Lluvia ligera" },
  63: { icon: CloudRain,     en: "Rain",          es: "Lluvia" },
  65: { icon: CloudRain,     en: "Heavy rain",    es: "Lluvia intensa" },
  71: { icon: CloudSnow,     en: "Light snow",    es: "Nieve ligera" },
  73: { icon: CloudSnow,     en: "Snow",          es: "Nieve" },
  75: { icon: CloudSnow,     en: "Heavy snow",    es: "Nieve intensa" },
  80: { icon: CloudRain,     en: "Rain showers",  es: "Chubascos" },
  81: { icon: CloudRain,     en: "Rain showers",  es: "Chubascos" },
  82: { icon: CloudRain,     en: "Violent showers", es: "Chubascos fuertes" },
  95: { icon: CloudLightning,en: "Thunderstorm",  es: "Tormenta" },
  96: { icon: CloudLightning,en: "Storm w/ hail", es: "Tormenta con granizo" },
  99: { icon: CloudLightning,en: "Storm w/ hail", es: "Tormenta con granizo" },
};
function meta(code, lang) {
  const m = CODE_META[code] || { icon: Cloud, en: "Unknown", es: "—" };
  return { Icon: m.icon, label: lang === "es" ? m.es : m.en };
}

const WEEKDAY = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  es: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
};

export default function WeatherCard({ projectId, hasLocation, onSetLocation }) {
  const { t, lang } = useLang();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!hasLocation) { setW(null); return; }
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/weather`);
      if (data?.error || !data?.current) {
        setErr(t("weather_temporarily_unavailable"));
        setW(null);
      } else {
        setW(data);
      }
    } catch (e) {
      setErr(t("weather_temporarily_unavailable"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [projectId, hasLocation]);

  if (!hasLocation) {
    return (
      <div className="surface-card p-6" data-testid="weather-card-empty">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-neutral-400" />
          <p className="label-eyebrow">{t("weather")}</p>
        </div>
        <p className="mt-3 text-sm text-neutral-500">{t("weather_no_location")}</p>
        {onSetLocation && (
          <button
            data-testid="set-location-button"
            onClick={onSetLocation}
            className="mt-3 text-sm text-[#B8933A] hover:text-[#8C6D22] font-medium">
            {t("set_project_location")} →
          </button>
        )}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="surface-card p-6">
        <div className="h-4 w-24 bg-neutral-100 animate-pulse rounded mb-4" />
        <div className="h-10 w-32 bg-neutral-100 animate-pulse rounded" />
      </div>
    );
  }
  if (err || !w) {
    return (
      <div className="surface-card p-6" data-testid="weather-error">
        <p className="label-eyebrow">{t("weather")}</p>
        <p className="mt-2 text-sm text-neutral-500">{err || "—"}</p>
        <button
          type="button"
          onClick={load}
          className="mt-3 text-sm text-[#B8933A] hover:text-[#8C6D22] font-medium"
          data-testid="weather-retry-button"
        >
          {t("retry")} →
        </button>
      </div>
    );
  }

  const cur = w.current || {};
  const daily = w.daily || {};
  const { Icon, label } = meta(cur.weather_code, lang);
  const days = (daily.time || []).map((d, i) => {
    const dt = new Date(d);
    const dow = WEEKDAY[lang][dt.getDay()];
    const { Icon: DI } = meta((daily.weather_code || [])[i], lang);
    return {
      dow, code: (daily.weather_code || [])[i], DI,
      max: Math.round((daily.temperature_2m_max || [])[i] ?? 0),
      min: Math.round((daily.temperature_2m_min || [])[i] ?? 0),
      pop: (daily.precipitation_probability_max || [])[i] ?? 0,
    };
  });

  return (
    <div className="surface-card p-6" data-testid="weather-card">
      <div className="flex items-center justify-between">
        <p className="label-eyebrow">{t("weather")}</p>
        <span className="text-[10px] text-neutral-400">Open-Meteo</span>
      </div>
      <div className="mt-3 flex items-center gap-4">
        <Icon className="w-12 h-12 text-[#B8933A]" strokeWidth={1.5} />
        <div>
          <p className="font-[Fraunces] text-4xl font-bold text-neutral-950 leading-none">
            {Math.round(cur.temperature_2m ?? 0)}°C
          </p>
          <p className="text-sm text-neutral-600 mt-1">{label}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-4 text-xs text-neutral-500">
        <span className="flex items-center gap-1"><Wind className="w-3.5 h-3.5" />{Math.round(cur.wind_speed_10m ?? 0)} km/h</span>
        <span className="flex items-center gap-1"><Droplets className="w-3.5 h-3.5" />{cur.relative_humidity_2m ?? 0}%</span>
      </div>
      {days.length > 0 && (
        <div className="mt-5 border-t border-neutral-100 pt-4 grid grid-cols-5 gap-2">
          {days.map((d, i) => (
            <div key={i} className="text-center" data-testid={`weather-day-${i}`}>
              <p className="text-[10px] uppercase tracking-wider text-neutral-500">{d.dow}</p>
              <d.DI className="w-5 h-5 text-neutral-600 mx-auto mt-1.5" strokeWidth={1.5} />
              <p className="text-xs font-medium text-neutral-900 mt-1">{d.max}°</p>
              <p className="text-[10px] text-neutral-400">{d.min}°</p>
              {d.pop > 20 && (
                <p className="text-[10px] text-sky-700 mt-0.5">{d.pop}%</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
